import type { ScrollMeasurement } from "../scrollLayout";

/** All values in scroll.offset units (0..1 of the page's max scroll), same
 *  convention as decksScrollWindows. */
export type RadioWindows = {
  /** Hero appearance: full at offset 0, gone by `exitEnd` (tablet/mobile —
   *  on desktop the model never exits, it travels instead). */
  hero: { exitStart: number; exitEnd: number };
  /** Order-section appearance. */
  order: {
    enterStart: number;
    enterEnd: number;
    exitStart: number;
    exitEnd: number;
  };
};

/** Desktop travel path: scroll offsets at which the model should be parked
 *  beside each content section (s1, s2, … anchors, ascending), plus the
 *  offset of the order stop. The component alternates the model between the
 *  left/right margins across consecutive waypoints. */
export type RadioPath = {
  sections: number[];
  order: number;
};

/** Crossfade window of one fixed text panel (decks-style), in scroll.offset
 *  units. Panels fade in as their placeholder section reaches the viewport
 *  and out as the next one takes over — only one panel is visible at a time. */
export type RadioTextWindow = {
  in: number;
  inEnd: number;
  out: number;
  outEnd: number;
};

/** Choreography fractions. Hero fractions span [0, anchors.s1] (the first
 *  feature section reaching the viewport top); order fractions span
 *  [anchors.order − vh, anchors.faq ?? anchors.end] (order entering from the
 *  bottom until the FAQ — or, once FAQ moved into a modal, the footer —
 *  reaches the viewport top). Tune feel here. */
const HERO = {
  exitStart: 0.3,
  exitEnd: 0.75,
} as const;
const ORDER = {
  enterStart: 0.2,
  enterEnd: 0.45,
  // Exit earlier within the span: the model must be fully gone before the FAQ
  // title rises, otherwise it sits centred over "FREQUENTLY ASKED QUESTIONS".
  // The desktop breathing gap before the FAQ (see RadioPageContent) gives this
  // window room while the device still holds beside the order copy (~0.55).
  exitStart: 0.62,
  exitEnd: 0.8,
} as const;

/** A section's travel waypoint: the model is parked beside the section when
 *  its anchor (section top) is this far from the viewport top. Sections are
 *  full-viewport panels with vertically centered copy, so the model should
 *  settle just as the panel top reaches the viewport top. */
const SECTION_DWELL_SCREENS = -0.1;

/** Fraction of a travel leg the model spends parked before it crosses to the
 *  other side. Shared between the model (RadioPart) and the text windows
 *  below so the copy fades out exactly when the model takes off. */
export const TRAVEL_CROSS_START = 0.62;

/** Text-panel timing relative to the model's stops: the copy rises while the
 *  model is landing (slightly before its dwell, finishing just after, where
 *  the pose damping actually settles) and fades over the first stretch of
 *  the next flight. All in screens / leg fractions. */
const TEXT = {
  inLead: 0.35, // start fading in this many screens before the dwell
  inTail: 0.05, // fully in this many screens after the dwell
  outLen: 0.3, // fade-out length as a fraction of the leg
} as const;

/** Rough mirror of the /spirit-box DOM (hero 100vh + long section column +
 *  order + footer; specs + FAQ now live in modals) — pre-measurement
 *  fallback only. */
export const DEFAULT_RADIO_MEASUREMENT: ScrollMeasurement = {
  vh: 1000,
  contentHeight: 17900,
  anchors: { s1: 1000, order: 14400, end: 16500 },
};

export function deriveRadioWindows(m: ScrollMeasurement): RadioWindows | null {
  const { vh } = m;
  if (vh <= 0) return null;
  const s1 = m.anchors.s1 / vh;
  const order = m.anchors.order / vh;
  const faq = (m.anchors.faq ?? m.anchors.end) / vh;
  if (![s1, order, faq].every(Number.isFinite)) return null;

  const maxScroll = Math.max(0.001, m.contentHeight / vh - 1);
  const toOffset = (screens: number) => screens / maxScroll;
  const seg = (start: number, len: number, f: number) => toOffset(start + f * len);

  const heroLen = Math.max(0.001, s1);
  const oStart = order - 1;
  const oLen = Math.max(0.001, faq - oStart);

  return {
    hero: {
      exitStart: seg(0, heroLen, HERO.exitStart),
      exitEnd: seg(0, heroLen, HERO.exitEnd),
    },
    order: {
      enterStart: seg(oStart, oLen, ORDER.enterStart),
      enterEnd: seg(oStart, oLen, ORDER.enterEnd),
      exitStart: seg(oStart, oLen, ORDER.exitStart),
      exitEnd: seg(oStart, oLen, ORDER.exitEnd),
    },
  };
}

/** Build the desktop travel path from the measured sN anchors. Anchors are
 *  matched by /^s(\d+)$/ and ordered by their index, so adding/removing a
 *  section in the HTML shifts the whole path with no constant changes. */
export function deriveRadioPath(m: ScrollMeasurement): RadioPath | null {
  const { vh } = m;
  if (vh <= 0) return null;
  const order = m.anchors.order / vh;
  if (!Number.isFinite(order)) return null;

  const maxScroll = Math.max(0.001, m.contentHeight / vh - 1);
  const toOffset = (screens: number) => screens / maxScroll;

  const sections = Object.keys(m.anchors)
    .map((key) => {
      const match = /^s(\d+)$/.exec(key);
      return match ? { i: Number(match[1]), px: m.anchors[key] } : null;
    })
    .filter((x): x is { i: number; px: number } => x !== null)
    .sort((a, b) => a.i - b.i)
    .map(({ px }) => toOffset(Math.max(0.2, px / vh + SECTION_DWELL_SCREENS)));
  if (sections.length === 0) return null;

  return {
    sections,
    order: toOffset(order - 0.5),
  };
}

/** Crossfade windows for the fixed text panels, keyed by section number
 *  (s1 → 1, …). Derived from the SAME dwell stops as the model's travel path
 *  so text and model stay in lock-step: the copy rises while the model lands
 *  at its dwell, and fades out exactly when the model takes off
 *  (TRAVEL_CROSS_START into the next leg). */
export function deriveRadioTextWindows(
  m: ScrollMeasurement,
): Record<number, RadioTextWindow> | null {
  const { vh } = m;
  if (vh <= 0) return null;
  const order = m.anchors.order / vh;
  if (!Number.isFinite(order)) return null;

  const maxScroll = Math.max(0.001, m.contentHeight / vh - 1);
  const toOffset = (screens: number) => screens / maxScroll;

  const sections = Object.keys(m.anchors)
    .map((key) => {
      const match = /^s(\d+)$/.exec(key);
      return match ? { n: Number(match[1]), px: m.anchors[key] } : null;
    })
    .filter((x): x is { n: number; px: number } => x !== null)
    .sort((a, b) => a.n - b.n);
  if (sections.length === 0) return null;

  // Dwell stops in screens — must mirror deriveRadioPath.
  const dwells = sections.map(({ px }) =>
    Math.max(0.2, px / vh + SECTION_DWELL_SCREENS),
  );
  const orderStop = order - 0.5;

  const windows: Record<number, RadioTextWindow> = {};
  for (let i = 0; i < sections.length; i++) {
    const dwell = dwells[i];
    const next = i + 1 < dwells.length ? dwells[i + 1] : orderStop;
    const leg = Math.max(0.001, next - dwell);
    const takeoff = dwell + TRAVEL_CROSS_START * leg;
    windows[sections[i].n] = {
      in: toOffset(dwell - TEXT.inLead),
      inEnd: toOffset(dwell + TEXT.inTail),
      out: toOffset(takeoff),
      outEnd: toOffset(takeoff + TEXT.outLen * leg),
    };
  }
  return windows;
}
